import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'
import store from '@/store'
import { buildLoginLocation, safeReplace } from '@/utils/redirect'

let authExpiredHandling = false
let authFlowInProgress = false
let authFlowStartToken = ''

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 30000
})

function isLoginRequest(config) {
  return config && String(config.url || '').indexOf('/auth/login') >= 0
}

function isCurrentUserRequest(config) {
  return config && String(config.url || '').indexOf('/users/me') >= 0
}

function createAuthExpiredError(message, options = {}) {
  const error = new Error(message || '登录已过期，请重新登录')
  error.authExpired = true
  error.silent = true
  Object.assign(error, options)
  return error
}

export function setAuthFlowInProgress(active) {
  authFlowInProgress = !!active
  authFlowStartToken = active ? (store.getters.token || '') : ''
}

function getResponseMessage(errorOrResponse, fallback) {
  const response = errorOrResponse && errorOrResponse.response
  const data = response ? response.data : errorOrResponse && errorOrResponse.data
  return (data && (data.message || data.errorMsg)) || errorOrResponse.message || fallback
}

function requestError(message, config) {
  const error = new Error(message || '操作失败，请稍后重试')
  if (!(config && config.legacySilentError)) {
    Message({ type: 'error', message: error.message, showClose: true, duration: 5000 })
    error.notified = true
  }
  return error
}

function checkLegacyResult(data, config) {
  if (data && typeof data.errorCode !== 'undefined' && Number(data.errorCode) !== 0) {
    throw requestError(data.errorMsg || data.message || '操作失败', config)
  }
  if (data && data.success === false) {
    throw requestError(data.message || data.errorMsg || '操作失败', config)
  }
}

function showOperationSuccess(config) {
  // Explicit write-operation metadata: POST list/search/poll requests stay quiet.
  if (config && config.successMessage) {
    Message({ type: 'success', message: config.successMessage, showClose: true, duration: 3500 })
  }
}

function isStaleTokenResponse(config) {
  const requestToken = config && typeof config.__authToken === 'string' ? config.__authToken : ''
  const currentToken = store.getters.token || ''
  if (currentToken && requestToken !== currentToken) {
    return true
  }
  return authFlowInProgress && requestToken === authFlowStartToken
}

function handleAuthExpired(message, navigate = true) {
  if (authExpiredHandling) {
    return
  }
  authExpiredHandling = true
  const loginLocation = buildLoginLocation(router.currentRoute.fullPath)
  store.dispatch('user/clearSession')
  Message.error(message || '登录已过期，请重新登录')
  const currentRoute = router.currentRoute
  if (navigate && currentRoute.path !== '/login') {
    safeReplace(router, loginLocation).catch(() => {})
  }
  setTimeout(() => {
    authExpiredHandling = false
  }, 1000)
}

service.interceptors.request.use(
  config => {
    const token = store.getters.token
    config.__authToken = token || ''
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

service.interceptors.response.use(
  response => {
    const config = response.config || {}
    const res = response.data
    if (!res || typeof res.code === 'undefined') {
      checkLegacyResult(res, config)
      showOperationSuccess(config)
      return config.returnResponse ? response : res
    }
    if (Number(res.code) === 0) {
      checkLegacyResult(res.data, config)
      showOperationSuccess(config)
      return config.returnResponse ? response : res.data
    }
    if (res.code === 401) {
      if (isLoginRequest(response.config)) {
        return Promise.reject(new Error(res.message || '登录失败'))
      }
      if (isStaleTokenResponse(response.config)) {
        return Promise.reject(createAuthExpiredError(res.message, { stale: true }))
      }
      if (isCurrentUserRequest(response.config)) {
        handleAuthExpired(res.message, false)
        return Promise.reject(createAuthExpiredError(res.message))
      }
      handleAuthExpired(res.message)
      return Promise.reject(createAuthExpiredError(res.message))
    }
    return Promise.reject(requestError(res.message || '请求失败', config))
  },
  error => {
    const status = error.response && error.response.status
    if (status === 401) {
      if (isLoginRequest(error.config)) {
        return Promise.reject(error)
      }
      const message = getResponseMessage(error, '登录已过期，请重新登录')
      if (isStaleTokenResponse(error.config)) {
        return Promise.reject(createAuthExpiredError(message, { stale: true }))
      }
      if (isCurrentUserRequest(error.config)) {
        handleAuthExpired(message, false)
        return Promise.reject(createAuthExpiredError(message))
      }
      handleAuthExpired(message)
      return Promise.reject(createAuthExpiredError(message))
    }
    if (!(error.config && (error.config.legacySilentError ||
      (status === 404 && error.config.legacySilent404)))) {
      const message = getResponseMessage(error, '网络异常，请稍后重试')
      Message({ type: 'error', message, showClose: true, duration: 5000 })
      error.notified = true
    }
    return Promise.reject(error)
  }
)

export default service
